import React, { memo, useMemo } from 'react';
import { graphql, useFragment } from 'react-relay';
import { ENTITY_ID } from '@atlassian/jira-navigation-apps-sidebar-nav4-context/src/common/constants.tsx';

import type {
	Nav4ProjectsSectionsWithDisplaySettings$data,
	Nav4ProjectsSectionsWithDisplaySettings$key,
} from '@atlassian/jira-relay/src/__generated__/Nav4ProjectsSectionsWithDisplaySettings.graphql';

import {
	ViewOptions,
	DefaultViewOption,
} from '@atlassian/jira-navigation-apps-sidebar-nav4-display-settings/src/ui/change-view-menu-item/view-options/constants.tsx';
import { useSidebarNav4 } from '@atlassian/jira-navigation-apps-sidebar-nav4-context/src/controllers/sidebar-context/index.tsx';
import type { DisplaySettingsPropertyKeysType } from '@atlassian/jira-navigation-apps-sidebar-nav4-display-settings/src/ui/display-settings-provider/types.tsx';
import type { ViewOptionsType } from '@atlassian/jira-navigation-apps-sidebar-nav4-display-settings/src/ui/change-view-menu-item/view-options/types.tsx';
import { DisplaySettingsPropertyKeys } from '@atlassian/jira-navigation-apps-sidebar-nav4-display-settings/src/ui/display-settings-provider/constants.tsx';
import type { FavoriteItemsType, RecentItemsType } from './types.tsx';
import { RenderStarredSection } from './starred-section/index.tsx';
import { RenderRecentSection } from './recent-section/index.tsx';

export type NavigationCustomisationNodes = NonNullable<
	NonNullable<
		Nav4ProjectsSectionsWithDisplaySettings$data['settings_navigationCustomisation']
	>['properties']
>['nodes'];

const isDisplaySettingsKey = (value: string): value is keyof typeof DisplaySettingsPropertyKeys => {
	const keys: String[] = Object.values(DisplaySettingsPropertyKeys);
	return keys.includes(value);
};

const getNavigationCustomisation = (
	nodes: NavigationCustomisationNodes,
	viewOption: DisplaySettingsPropertyKeysType,
	defaultValue: ViewOptionsType,
) => {
	return (
		nodes?.find(
			(node) =>
				node?.key &&
				isDisplaySettingsKey(node.key) &&
				DisplaySettingsPropertyKeys[node.key] === viewOption,
		)?.value || defaultValue
	);
};

const Nav4ProjectsSectionsWithDisplaySettingsInternal = ({
	favoriteItems,
	recentItems,
	queryRef,
	canCreateProject,
}: {
	favoriteItems: FavoriteItemsType;
	recentItems: RecentItemsType;
	queryRef: Nav4ProjectsSectionsWithDisplaySettings$key;
	canCreateProject: boolean | undefined | null;
}): React.JSX.Element => {
	const data = useFragment<Nav4ProjectsSectionsWithDisplaySettings$key>(
		graphql`
			fragment Nav4ProjectsSectionsWithDisplaySettings on Query
			@argumentDefinitions(entityAri: { type: "ID!" }, ownerAri: { type: "ID!" }) {
				settings_navigationCustomisation(entityAri: $entityAri, ownerAri: $ownerAri) {
					properties {
						nodes {
							key
							value
						}
					}
				}
			}
		`,
		queryRef,
	);

	const selectedViewOption = getNavigationCustomisation(
		data.settings_navigationCustomisation?.properties?.nodes,
		DisplaySettingsPropertyKeys.VIEW_OPTION,
		DefaultViewOption,
	);

	const { isSelectedPath } = useSidebarNav4();

	const isStarredViewOptionSelected = selectedViewOption === ViewOptions.STARRED;
	const isStarredAndRecentViewOptionSelected =
		selectedViewOption === ViewOptions.STARRED_AND_RECENT;

	// If the current project is not starred, display it under the Recent section.
	// Once navigated away from the current non-starred project, it should disappear from the sidebar,
	// along with the Recent section
	const currentRecentItem = recentItems.list.find(
		(item) => item.key && isSelectedPath(ENTITY_ID.PROJECT(item.key)),
	);

	const displayedRecentItems = useMemo(
		() =>
			isStarredViewOptionSelected
				? {
						list: currentRecentItem ? [currentRecentItem] : [],
						hasItems: Boolean(currentRecentItem),
					}
				: recentItems,
		[currentRecentItem, isStarredViewOptionSelected, recentItems],
	);

	const shouldShowStarredSection =
		isStarredAndRecentViewOptionSelected || isStarredViewOptionSelected;

	const shouldShowRecentSection =
		isStarredAndRecentViewOptionSelected || displayedRecentItems.hasItems;

	return (
		<>
			{shouldShowStarredSection && (
				<RenderStarredSection favoriteItems={favoriteItems} canCreateProject={canCreateProject} />
			)}
			{shouldShowRecentSection && (
				<RenderRecentSection
					recentItems={displayedRecentItems}
					canCreateProject={canCreateProject}
				/>
			)}
		</>
	);
};

export const Nav4ProjectsSectionsWithDisplaySettings = memo(
	Nav4ProjectsSectionsWithDisplaySettingsInternal,
);
